import { useMemo, useState } from "react";
import { FileCheck2, Receipt } from "lucide-react";
import { eurExact, shortDate } from "./format";

const FILTERS = [
  { id: "all", label: "This month's activity" },
  { id: "written", label: "Orders written" },
  { id: "po", label: "PO received" },
  { id: "invoiced", label: "Invoiced" },
  { id: "open", label: "Not yet invoiced" },
  { id: "zero", label: "Zero value" },
];

const statusCls = (s) => {
  const v = (s || "").toLowerCase();
  if (v.includes("invoiced")) return "bg-emerald-400/15 text-emerald-300 ring-emerald-400/30";
  if (v.includes("invoicing")) return "bg-amber-400/15 text-amber-300 ring-amber-400/30";
  if (v.includes("dispatched") || v.includes("delivered")) return "bg-sky-400/15 text-sky-300 ring-sky-400/30";
  return "bg-slate-400/15 text-slate-300 ring-slate-400/30";
};

export const OrdersTable = ({ orders, month, invoicedBasis = "status" }) => {
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const invoicedThisMonth = (o) => (invoicedBasis === "report" ? o.invoiced_via === "report" : o.invoiced_at.startsWith(month));
    const touched = (o) => o.date_received.startsWith(month) || (o.customer_po && o.po_received_at.startsWith(month)) || invoicedThisMonth(o);
    return orders.filter((o) => {
      if (filter === "all" && !touched(o)) return false;
      if (filter === "written" && !o.date_received.startsWith(month)) return false;
      if (filter === "po" && !(o.customer_po && o.po_received_at.startsWith(month))) return false;
      if (filter === "invoiced" && !invoicedThisMonth(o)) return false;
      if (filter === "open" && !o.is_open) return false;
      if (filter === "zero" && !(o.is_open && o.total === 0)) return false;
      if (!q) return true;
      return [o.job_id, o.customer, o.product, o.customer_po, o.rep].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [orders, filter, query, month, invoicedBasis]);

  const total = rows.reduce((s, o) => s + (o.total || 0), 0);
  const cost = rows.reduce((s, o) => s + (o.cost || 0), 0);

  return (
    <div className="min-w-0 rounded-2xl bg-slate-900/70 ring-1 ring-white/5" data-testid="sales-orders-table">
      <div className="flex flex-col gap-3 border-b border-white/5 p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`sales-filter-${f.id}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide ring-1 transition-colors ${
                filter === f.id ? "bg-cyan-400/15 text-cyan-300 ring-cyan-400/40" : "text-slate-400 ring-white/10 hover:text-slate-200 hover:ring-white/20"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search job, customer, PO, rep…"
          data-testid="sales-orders-search"
          className="w-full rounded-lg bg-slate-800/80 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 ring-1 ring-white/10 focus:outline-none focus:ring-cyan-400/50 sm:w-64"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
              <th className="px-5 py-3">Job #</th>
              <th className="px-2 py-3">Rep</th>
              <th className="px-3 py-3">Customer</th>
              <th className="hidden px-3 py-3 md:table-cell">Description</th>
              <th className="px-3 py-3 text-right">Value</th>
              <th className="hidden px-3 py-3 text-right xl:table-cell">Cost</th>
              <th className="hidden px-3 py-3 text-right xl:table-cell">Margin</th>
              <th className="hidden px-3 py-3 lg:table-cell">Customer PO</th>
              <th className="px-3 py-3">Status</th>
              <th className="hidden px-3 py-3 sm:table-cell">Received</th>
              <th className="hidden px-5 py-3 lg:table-cell">Invoiced</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.length === 0 && (
              <tr><td colSpan={11} className="px-5 py-10 text-center text-slate-500" data-testid="sales-orders-empty">No orders match this view.</td></tr>
            )}
            {rows.map((o) => (
              <tr key={o.job_id} className="transition-colors hover:bg-white/[0.03]" data-testid={`sales-order-row-${o.job_id}`}>
                <td className="px-5 py-3 font-mono font-bold text-cyan-300">#{o.job_id}</td>
                <td className="px-2 py-3 font-heading text-xs font-black tracking-wide text-slate-300" data-testid={`sales-order-rep-${o.job_id}`}>{o.rep || <span className="font-normal text-slate-600">—</span>}</td>
                <td className="max-w-[140px] truncate px-3 py-3 font-medium text-slate-100 sm:max-w-[220px]" title={o.customer}>{o.customer}</td>
                <td className="hidden max-w-[280px] truncate px-3 py-3 text-slate-400 md:table-cell" title={o.product}>{o.product}</td>
                <td className={`px-3 py-3 text-right font-mono font-semibold ${o.total === 0 && o.is_open ? "text-rose-300" : "text-slate-100"}`}>{eurExact(o.total)}</td>
                <td className="hidden px-3 py-3 text-right font-mono text-rose-300/90 xl:table-cell" title={o.suppliers?.join(", ")} data-testid={`sales-order-cost-${o.job_id}`}>{o.cost ? eurExact(o.cost) : <span className="text-slate-600">—</span>}</td>
                <td className={`hidden px-3 py-3 text-right font-mono xl:table-cell ${o.margin < 0 ? "text-rose-300" : "text-amber-200/90"}`}>{o.cost ? eurExact(o.margin) : <span className="text-slate-600">—</span>}</td>
                <td className="hidden px-3 py-3 lg:table-cell">
                  {o.customer_po ? (
                    <span className="inline-flex items-center gap-1.5 text-amber-300" title={`PO received ${shortDate(o.po_received_at)}`}>
                      <FileCheck2 className="h-3.5 w-3.5" /><span className="font-mono text-xs">{o.customer_po}</span>
                    </span>
                  ) : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-3 py-3">
                  <span className={`inline-block max-w-[180px] truncate rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${statusCls(o.status)}`} title={o.status}>{o.status || "—"}</span>
                </td>
                <td className="hidden px-3 py-3 text-slate-400 sm:table-cell">{shortDate(o.date_received)}</td>
                <td className="hidden px-5 py-3 lg:table-cell">
                  {o.invoiced_at ? (
                    <span className="inline-flex items-center gap-1.5 text-emerald-300" title={o.invoiced_via === "report" ? `From invoice report${o.invoice_net !== null && o.invoice_net !== undefined ? ` · net ${eurExact(o.invoice_net)}` : ""}` : "Date the status changed to Invoiced"}>
                      <Receipt className="h-3.5 w-3.5" />{shortDate(o.invoiced_at)}
                      {o.invoice_numbers?.length > 0 && <span className="font-mono text-xs text-emerald-400/80" data-testid={`sales-order-invoice-${o.job_id}`}>#{o.invoice_numbers.join(", #")}</span>}
                      {o.invoiced_via !== "report" && <span className="text-[10px] text-slate-500">status</span>}
                    </span>
                  ) : <span className="text-slate-600">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t border-white/10 text-sm">
                <td className="px-5 py-3 text-slate-400" colSpan={4}>
                  <span className="font-mono font-semibold text-slate-200" data-testid="sales-orders-row-count">{rows.length}</span> order{rows.length === 1 ? "" : "s"} shown
                </td>
                <td className="px-3 py-3 text-right font-mono font-bold text-slate-100" data-testid="sales-orders-total">{eurExact(total)}</td>
                <td className="hidden px-3 py-3 text-right font-mono font-bold text-rose-300 xl:table-cell" data-testid="sales-orders-cost-total">{eurExact(cost)}</td>
                <td className="hidden px-3 py-3 text-right font-mono font-bold text-amber-200 xl:table-cell" data-testid="sales-orders-margin-total">{eurExact(total - cost)}</td>
                <td colSpan={4} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};
